import { useTranslation } from 'react-i18next';
import ui from '../styles/ui.module.css';
import { usePageTitle } from '../hooks/usePageTitle';
import styles from './LegalPage.module.css';

// Long-form trust pages (privacy, terms), rendered from
// `legal.<doc>.sections` in the locale file.

export type LegalDoc = 'privacy' | 'terms';

interface Section {
  heading: string;
  body: string[];
}

const isSections = (value: unknown): value is Section[] =>
  Array.isArray(value) &&
  value.every(
    (s) => typeof s?.heading === 'string' && Array.isArray(s?.body) && s.body.every((p: unknown) => typeof p === 'string'),
  );

// Bump when the text changes materially.
const LAST_UPDATED = '2026-11-04';

export default function LegalPage({ doc }: { doc: LegalDoc }) {
  const { t } = useTranslation();
  const sections = t(`legal.${doc}.sections`, { returnObjects: true });
  usePageTitle(t(`legal.${doc}.title`));

  return (
    <main className={ui.page}>
      <div className={ui.prose}>
        <h1 className={styles.title}>{t(`legal.${doc}.title`)}</h1>
        <p role="note" className={styles.draft}>
          {t('legal.draftNotice')}
        </p>
        <p className={ui.hint}>{t('legal.lastUpdated', { date: LAST_UPDATED })}</p>
        {isSections(sections) &&
          sections.map((section) => (
            <section key={section.heading}>
              <h2>{section.heading}</h2>
              {section.body.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </section>
          ))}
      </div>
    </main>
  );
}
